import React, { useState } from "react";
import "./TextInput.css";
import { IoIosSearch } from "react-icons/io";

const TextInput = ({
  type,
  placeholderText,
  innerLabel,
  width,
  idName,
  inputName,
  inputValue,
  handleChange,
  inputRef,
  isRequired,
  selectOptions,
  textRows,
  accept,
  defaultValue,
  error,
  onFocus,
  readOnly,
}) => {
  const [passType, setPassType] = useState("password");

  const isAllowedControlKey = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
    ];
    if (allowedKeys.includes(e.key)) return true;
    if (e.ctrlKey || e.metaKey) return true; // allow shortcuts like Ctrl+C/V/X/A
    return false;
  };

  const handleNumericKeyDown = (e) => {
    if (isAllowedControlKey(e)) return;
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleTelChange = (e) => {
    const digitsOnly = (e.target.value || "").replace(/\D/g, "");
    if (digitsOnly !== e.target.value) {
      e.target.value = digitsOnly;
    }
    handleChange(e);
  };

  const computeNextValueAfterPaste = (pasteText) => {
    const pasteDigits = (pasteText || "").replace(/\D/g, "");
    const currentValue = inputValue || "";
    const el = inputRef && inputRef.current ? inputRef.current : null;
    if (
      el &&
      typeof el.selectionStart === "number" &&
      typeof el.selectionEnd === "number"
    ) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      return (
        currentValue.slice(0, start) + pasteDigits + currentValue.slice(end)
      );
    }
    return `${currentValue}${pasteDigits}`;
  };

  const handleTelPaste = (e) => {
    const text = e.clipboardData.getData("text");
    const digitsOnly = (text || "").replace(/\D/g, "");
    if (digitsOnly !== text) {
      e.preventDefault();
      const nextValue = computeNextValueAfterPaste(text);
      handleChange({ target: { name: inputName, value: nextValue } });
    }
  };

  return (
    <>
      {type === "select" ? (
        <select
          className={`${error} selectInput`}
          id={idName}
          name={inputName}
          required={isRequired}
          value={inputValue}
          onChange={handleChange}
          ref={inputRef}
          defaultValue={defaultValue}
        >
          <option className="text-[#C4C4C4] text-sm" value="" disabled>
            {placeholderText}
          </option>
          {selectOptions.map((selectOption) => {
            return (
              <option
                className="font-medium text-sm capitalize text-[#666]"
                key={selectOption}
                value={selectOption}
              >
                {selectOption}
              </option>
            );
          })}
        </select>
      ) : type === "textbox" ? (
        <textarea
          className={`${error} textArea`}
          rows={textRows}
          placeholder={placeholderText}
          required={isRequired}
          value={inputValue}
          onChange={handleChange}
          ref={inputRef}
        />
      ) : type === "file" ? (
        <div className="fileInputContainer">
          <label className="fileInputPlaceholder">{placeholderText}</label>
          <input
            className={`${error} fileInput`}
            name={inputName}
            type="file"
            style={{ width: `${width}` }}
            value={inputValue}
            onChange={handleChange}
            required={isRequired}
            accept={accept}
            ref={inputRef}
          />
        </div>
      ) : type === "password" ? (
        <div className="relative flex items-center">
          <input
            className={`${error} textInput`}
            name={inputName}
            type={passType}
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
          <img
            src={`/img/eye-${passType === "text" ? "open" : "close"}.png`}
            alt="eye"
            className="absolute w-[1.5rem] right-5 z-10 cursor-pointer"
            onClick={() =>
              passType === "password"
                ? setPassType("text")
                : setPassType("password")
            }
          />
        </div>
      ) : type === "search" ? (
        <div className="relative flex items-center">
          <input
            className={`${error} textInput pr-10`}
            name={inputName}
            type="text"
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
          <IoIosSearch className="absolute w-6 h-6 right-3 text-gray-600" />
        </div>
      ) : type === "tel" ? (
        <div
          className={`flex items-center w-full ${
            innerLabel ? "gap-1 bg-[#F5F5F5] rounded-lg px-3 text-sm" : ""
          }`}
        >
          {innerLabel}
          <input
            className={`${error} textInput`}
            name={inputName}
            type="tel"
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleTelChange}
            onFocus={onFocus}
            onKeyDown={handleNumericKeyDown}
            onPaste={handleTelPaste}
            inputMode="numeric"
            pattern="[0-9]*"
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
          />
        </div>
      ) : (
        <div
          className={`flex items-center w-full ${
            innerLabel ? "gap-1 bg-[#F5F5F5] rounded-lg px-3 text-sm" : ""
          }`}
        >
          {innerLabel}
          <input
            className={`${error} textInput`}
            name={inputName}
            type={type}
            style={{ width: `${width}` }}
            placeholder={placeholderText}
            value={inputValue}
            onChange={handleChange}
            onFocus={onFocus}
            required={isRequired}
            defaultValue={defaultValue}
            ref={inputRef}
            readOnly={readOnly}
          />
        </div>
      )}
      {error && <div className="text-xs text-[#f00] mt-1">{error}</div>}
    </>
  );
};

TextInput.defaultProps = {
  type: "text",
  idName: "",
  inputName: "",
  placeholderText: "",
  width: "100%",
  handleChange: () => {},
  isRequired: false,
  selectOptions: [],
};

export default TextInput;
